import {
  Heading,
  Stack,
  Text,
  Inline,
  Box,
  Alert,
  Hidden,
} from 'braid-design-system';
import { CertsyVerifiedIndicator, DefinitionList } from '@seek/profile-base-ui';
import ProfileSection from './ProfileSection';
import Qualifications from './Qualifications';
import Roles from './Roles';
import Skills from './Skills';
import Licences from './Licences';
import type { Profile } from '../service/types';
import { useTranslations } from '@vocab/react';
import translations from '../.vocab';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import type { SalaryType } from 'src/App/AppPageBlock/graphql/cm-types.generated';
import { formatSalaryValues } from 'src/libs/formatSalaryValues';
import { useUserActionStatus } from 'src/components/common/UserActionStatusContext';
import { trackAlertDismissEvent } from 'src/utils/events';

interface Props {
  profile: Profile;
  jobApplicationId: string;
  shouldDisplayProfileSalarySection: boolean;
}

const ProfileComponent = ({
  profile,
  jobApplicationId,
  shouldDisplayProfileSalarySection,
}: Props) => {
  const { t } = useTranslations(translations);
  const { site, locale } = useAppConfig();
  const { getCertsyVerifiedFullNameAlert, hideCertsyVerifiedFullNameAlert } =
    useUserActionStatus('certsy-verified-full-name-alert');
  const completeExpectedSalaries = profile.expectedSalaries.filter(
    (
      value,
    ): value is {
      salaryType: SalaryType;
      minimumSalaryValue: number;
      maximumSalaryValue: number | null;
      currency: string;
    } =>
      !(
        value.currency === null ||
        value.salaryType === null ||
        value.minimumSalaryValue === null
      ),
  );

  const certsyVerifiedFullName = profile.verifiedIdentity?.fullName;

  const isNationalityDisplayed = profile.nationalities.length > 0;

  const getRightsToWorkValue = (): Array<{ key: string; text: string }> => {
    if (profile.rightsToWorkV2.length > 0) {
      return profile.rightsToWorkV2.map(({ id, displayLabel }) => ({
        key: id,
        text: `${displayLabel}`,
      }));
    }
    return profile.rightsToWork.map(({ country, answer }) => ({
      key: country,
      text: `${country} - ${answer}`,
    }));
  };

  const rightsToWork = getRightsToWorkValue();

  const isRightsToWorkDisplayed =
    (site === 'employer-seek-my' ||
      site === 'employer-seek-sg' ||
      site === 'employer-seek-hk' ||
      site === 'employer-seek-th') &&
    rightsToWork.length > 0;

  const isExpectedSalaryDisplayed =
    shouldDisplayProfileSalarySection &&
    (site === 'employer-seek-my' ||
      site === 'employer-seek-sg' ||
      site === 'employer-seek-ph' ||
      site === 'employer-seek-id' ||
      site === 'employer-seek-hk' ||
      site === 'employer-seek-th') &&
    completeExpectedSalaries.length > 0;

  return (
    <Stack space="medium" dividers data={{ testid: 'profile-data' }}>
      {(isNationalityDisplayed ||
        isExpectedSalaryDisplayed ||
        isRightsToWorkDisplayed) && (
        <ProfileSection title={t('Employment Status')}>
          <Stack space="large">
            {isNationalityDisplayed && (
              <Stack space="small">
                <Stack space="medium">
                  <Stack space="small">
                    <Heading level="4">{t('Nationality')}</Heading>
                    {profile.nationalities.map((nationality: string) => (
                      <Text key={nationality}>{nationality}</Text>
                    ))}
                  </Stack>
                </Stack>
              </Stack>
            )}

            {isRightsToWorkDisplayed && (
              <Stack space="small">
                <Heading level="4">{t('Right to work')}</Heading>
                <Stack space="small">
                  {rightsToWork.map(({ key, text }) => (
                    <Text key={key}>{`${text}`}</Text>
                  ))}
                </Stack>
              </Stack>
            )}

            {isExpectedSalaryDisplayed && (
              <Stack space="small">
                <Heading level="4">{t('Salary expectation')}</Heading>
                <Stack space="small">
                  {completeExpectedSalaries.map(
                    ({
                      currency,
                      salaryType,
                      minimumSalaryValue,
                      maximumSalaryValue,
                    }) => (
                      <Text key={currency}>
                        {`${formatSalaryValues({
                          minimumSalaryValue,
                          maximumSalaryValue,
                          currency,
                          locale,
                        })} ${salaryType !== null && t(salaryType)}`}
                      </Text>
                    ),
                  )}
                </Stack>
              </Stack>
            )}
          </Stack>
        </ProfileSection>
      )}

      {profile.roles.length > 0 && (
        <ProfileSection title={t('Work history')}>
          <Roles roles={profile.roles} />
        </ProfileSection>
      )}
      {profile.qualifications.length > 0 && (
        <ProfileSection title={t('Qualifications')}>
          <Qualifications qualifications={profile.qualifications} />
        </ProfileSection>
      )}
      {certsyVerifiedFullName ? (
        <ProfileSection
          title={
            <Inline space="xsmall" alignY="center">
              <Heading level="3">{t('Digital ID')}</Heading>
              <CertsyVerifiedIndicator idSuffix={jobApplicationId} />
            </Inline>
          }
        >
          <Stack space="medium">
            <Hidden below="desktop">
              <DefinitionList
                data={[
                  { name: t('Full name'), values: [certsyVerifiedFullName] },
                ]}
              />
            </Hidden>
            <Hidden above="tablet">
              <Box
                component="dl"
                style={{
                  display: 'flex',
                  flexFlow: 'wrap',
                }}
              >
                <Box
                  component="dt"
                  style={{
                    flexBasis: '55%',
                    paddingRight: 'medium',
                  }}
                  paddingBottom={{ mobile: 'small', tablet: 'none' }}
                >
                  <Text tone="secondary">{t('Full name')}</Text>
                </Box>
                <Box component="dd" paddingBottom="none">
                  <Text>{[certsyVerifiedFullName]}</Text>
                </Box>
              </Box>
            </Hidden>

            {getCertsyVerifiedFullNameAlert() === 'visible' && (
              <Box
                paddingTop={['xsmall', 'small', 'medium']}
                paddingBottom={['medium', 'xsmall', 'none']}
                paddingX={['xsmall', 'none']}
              >
                <Alert
                  tone="info"
                  onClose={() => {
                    trackAlertDismissEvent({
                      alertShownMessageCode:
                        'Candidate Management - Verified ID Banner',
                    });
                    hideCertsyVerifiedFullNameAlert();
                  }}
                  closeLabel={t('Close')}
                >
                  <Text>
                    {t(
                      "A verified identity means this person has confirmed their identity with SEEK's verification platform using a government-issued document. This may differ from their SEEK profile name as they may be using a preferred name.",
                    )}
                  </Text>
                </Alert>
              </Box>
            )}
          </Stack>
        </ProfileSection>
      ) : null}
      {profile.licences.length > 0 && (
        <ProfileSection title={t('Licences and certifications')}>
          <Licences licences={profile.licences} />
        </ProfileSection>
      )}
      {profile.skills.length > 0 && (
        <ProfileSection title={t('Skills')}>
          <Skills skills={profile.skills} />
        </ProfileSection>
      )}
    </Stack>
  );
};

export default ProfileComponent;
